import React, { useState, useEffect } from "react";
import { post, fmt, shortSvc } from "../api";
import { useDashboard, type AppliedFilters } from "../context/DashboardContext";

interface FilterGroupProps {
  title: string;
  items: { value: string; label: string; sub?: string }[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

function FilterGroup({ title, items, selected, onToggle, onSelectAll, onClearAll }: FilterGroupProps) {
  const [search, setSearch] = useState("");
  const filtered = search
    ? items.filter(i => i.label.toLowerCase().includes(search.toLowerCase()) || i.value.includes(search))
    : items;

  return (
    <div className="flex-1 min-w-[200px]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{title}</span>
        <div className="flex gap-2">
          <button onClick={onSelectAll} className="text-[10px] text-blue-400 hover:text-blue-300">All</button>
          <button onClick={onClearAll} className="text-[10px] text-gray-500 hover:text-gray-300">None</button>
        </div>
      </div>
      {items.length > 5 && (
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search..." className="w-full px-2 py-1 mb-1.5 rounded border border-[#2a2d3e] bg-[#0f1117] text-xs outline-none focus:border-blue-500 text-gray-300 placeholder-gray-600" />
      )}
      <div className="max-h-[180px] overflow-y-auto space-y-0.5 pr-1 custom-scrollbar">
        {filtered.map(item => (
          <label key={item.value}
            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors text-sm
              ${selected.has(item.value) ? "bg-blue-500/10 text-blue-300" : "hover:bg-[#252840] text-gray-300"}`}>
            <input type="checkbox" checked={selected.has(item.value)} onChange={() => onToggle(item.value)}
              className="w-3.5 h-3.5 rounded border-gray-600 bg-[#0f1117] text-blue-500 focus:ring-0 focus:ring-offset-0 accent-blue-500" />
            <span className="truncate flex-1">{item.label}</span>
            {item.sub && <span className="text-[10px] text-gray-500 ml-auto shrink-0">{item.sub}</span>}
          </label>
        ))}
        {filtered.length === 0 && <div className="text-xs text-gray-600 px-2 py-2">No matches</div>}
      </div>
      {selected.size > 0 && (
        <div className="mt-1 text-[10px] text-blue-400">{selected.size} selected</div>
      )}
    </div>
  );
}

export default function FilterPanel() {
  const { customer, dateParams, filters, setFilters, clearFilters, hasFilters } = useDashboard();

  // Options loaded from unfiltered queries
  const [accountOpts, setAccountOpts] = useState<any[]>([]);
  const [serviceOpts, setServiceOpts] = useState<any[]>([]);
  const [regionOpts, setRegionOpts] = useState<any[]>([]);

  // Pending selections (not yet applied)
  const [pendingAccounts, setPendingAccounts] = useState<Set<string>>(new Set());
  const [pendingServices, setPendingServices] = useState<Set<string>>(new Set());
  const [pendingRegions, setPendingRegions] = useState<Set<string>>(new Set());

  const [expanded, setExpanded] = useState(false);

  const dateKey = JSON.stringify(dateParams);

  // Load filter options (always unfiltered to show all choices)
  useEffect(() => {
    if (!customer) return;
    if (!dateParams.month && !dateParams.start_date) return;
    const base = { customer, ...dateParams };
    post("/api/query/by-account", base).then(setAccountOpts).catch(() => {});
    post("/api/query/by-service", { ...base, limit: 50 }).then(setServiceOpts).catch(() => {});
    post("/api/query/by-region", base).then(setRegionOpts).catch(() => {});
  }, [customer, dateKey]);

  // Sync pending state when applied filters change (e.g. clear)
  useEffect(() => {
    setPendingAccounts(new Set(filters.accountIds));
    setPendingServices(new Set(filters.services));
    setPendingRegions(new Set(filters.regions));
  }, [filters]);

  const toggle = (set: Set<string>, value: string): Set<string> => {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    return next;
  };

  const isDirty =
    JSON.stringify([...pendingAccounts].sort()) !== JSON.stringify([...filters.accountIds].sort()) ||
    JSON.stringify([...pendingServices].sort()) !== JSON.stringify([...filters.services].sort()) ||
    JSON.stringify([...pendingRegions].sort()) !== JSON.stringify([...filters.regions].sort());

  const applyFilters = () => {
    setFilters({
      accountIds: [...pendingAccounts],
      services: [...pendingServices],
      regions: [...pendingRegions],
    });
  };

  const handleClear = () => {
    setPendingAccounts(new Set());
    setPendingServices(new Set());
    setPendingRegions(new Set());
    clearFilters();
  };

  const totalSelected = pendingAccounts.size + pendingServices.size + pendingRegions.size;

  return (
    <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl overflow-hidden">
      {/* Header bar — always visible */}
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#252840] transition-colors">
        <div className="flex items-center gap-3">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Filters</span>
          {hasFilters && (
            <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-medium">
              {filters.accountIds.length + filters.services.length + filters.regions.length} active
            </span>
          )}
        </div>
        {hasFilters && (
          <button onClick={e => { e.stopPropagation(); handleClear(); }}
            className="text-[10px] text-gray-500 hover:text-red-400 transition-colors">
            Clear all
          </button>
        )}
      </button>

      {/* Expandable filter body */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#2a2d3e]">
          <div className="flex flex-wrap gap-6 mt-3">
            <FilterGroup
              title={`Accounts (${accountOpts.length})`}
              items={accountOpts.map(a => ({ value: a.account_id, label: a.account_name || a.account_id, sub: fmt(a.cost) }))}
              selected={pendingAccounts}
              onToggle={v => setPendingAccounts(toggle(pendingAccounts, v))}
              onSelectAll={() => setPendingAccounts(new Set(accountOpts.map(a => a.account_id)))}
              onClearAll={() => setPendingAccounts(new Set())}
            />
            <FilterGroup
              title={`Services (${serviceOpts.length})`}
              items={serviceOpts.map(s => ({ value: s.service, label: shortSvc(s.service), sub: fmt(s.cost) }))}
              selected={pendingServices}
              onToggle={v => setPendingServices(toggle(pendingServices, v))}
              onSelectAll={() => setPendingServices(new Set(serviceOpts.map(s => s.service)))}
              onClearAll={() => setPendingServices(new Set())}
            />
            <FilterGroup
              title={`Regions (${regionOpts.length})`}
              items={regionOpts.map(r => ({ value: r.region, label: r.region || "global", sub: fmt(r.cost) }))}
              selected={pendingRegions}
              onToggle={v => setPendingRegions(toggle(pendingRegions, v))}
              onSelectAll={() => setPendingRegions(new Set(regionOpts.map(r => r.region)))}
              onClearAll={() => setPendingRegions(new Set())}
            />
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#2a2d3e]">
            <button onClick={applyFilters} disabled={!isDirty}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${isDirty
                  ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20"
                  : "bg-[#2a2d3e] text-gray-500 cursor-not-allowed"}`}>
              Apply Filter{totalSelected > 0 ? ` (${totalSelected})` : ""}
            </button>
            <button onClick={handleClear}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-[#2a2d3e] hover:border-gray-500 transition-all">
              Clear All
            </button>
            {isDirty && <span className="text-[10px] text-amber-400">Unsaved changes</span>}
          </div>
        </div>
      )}
    </div>
  );
}
